import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase'; 
import { 
    collection, 
    query, 
    orderBy, 
    limit, 
    onSnapshot, 
    addDoc, 
    serverTimestamp,
    updateDoc, 
    doc,
    writeBatch,
    getDoc,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext'; 
import './Chat.css'; // Keep external CSS for general app layout

// 🛑 CLOUDINARY CONFIGURATION 🛑
const CLOUDINARY_CLOUD_NAME = 'dift6go5s';
const CLOUDINARY_UPLOAD_PRESET = 'chat_preset'; 
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`;
// 🛑 END CLOUDINARY CONFIGURATION 🛑


// ======================================
// === CONSTANTS AND HELPERS (OUTSIDE) ===
// ======================================
const MESSAGE_LIMIT = 8;
const SCROLL_THRESHOLD = 150; 
 
const getInitials = (name) => {
    if (!name || name === 'Staff Member' || name === 'System Bot') return 'ST';
    const parts = name.split(' ').filter(p => p.length > 0);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const isUserOnline = (userId) => {
    if (userId === 'SYSTEM_BOT') return true;
    return Math.random() < 0.7; // Mocked online status
};

const Avatar = ({ name, isMyAvatar }) => {
    const initials = getInitials(name);
    return (
        <div 
            className={`chat-avatar ${isMyAvatar ? 'my-avatar' : ''}`}
            style={{
                width: '30px', 
                height: '30px', 
                borderRadius: '50%', 
       
                backgroundColor: isMyAvatar ? 'var(--color-primary, #1e90ff)' : '#ccc',
                color: isMyAvatar ? 'white' : '#333',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
             
                fontWeight: 'bold',
                fontSize: '0.9em',
                flexShrink: 0,
                marginRight: isMyAvatar ? '0' : '10px',
                marginLeft: isMyAvatar ? '10px' : '0',
            }}
        
        >
            {initials}
        </div>
    );
};

// Bot logic (omitted for brevity)
const checkAndSendBotReply = async (chatId, hotelId, guestMessage) => {
    const messageLower = guestMessage.toLowerCase();
    let replyText = null;

    let wifiSSID = 'GrandHotel_Guest';
    let wifiPassword = 'Welcome123';
    try {
        const settingsDocRef = doc(db, "hotelSettings", hotelId); 
        const settingsSnap = await getDoc(settingsDocRef);
        if (settingsSnap.exists()) {
            const settings = settingsSnap.data();
            wifiSSID = settings.wifiSSID || wifiSSID;
            wifiPassword = settings.wifiPassword || wifiPassword;
        }
    } catch (error) {
        console.error("Error fetching settings for bot:", error);
    }
    
    if (messageLower.includes('towels') || messageLower.includes('towel')) {
        replyText = "We've registered your request for fresh towels! Housekeeping will deliver them shortly. Thank you.";
    } else if (messageLower.includes('wifi') || messageLower.includes('password') || messageLower.includes('internet')) {
        replyText = `The hotel WiFi network is **'${wifiSSID}'**.
The password is **'${wifiPassword}'**. Please let us know if you have any issues connecting!`;
    } 

    if (replyText) {
        await addDoc(collection(db, "chats", chatId, "messages"), {
            text: replyText,
            senderId: 'SYSTEM_BOT',
            senderRole: 'staff',
            timestamp: serverTimestamp(),
            read: false, 
        });
        await updateDoc(doc(db, 'chats', chatId), {
            lastMessageText: `[Auto-Reply] ${replyText}`,
            lastMessageTimestamp: serverTimestamp(),
            guestHasUnread: true, 
        });
        return true; 
    }
    return false; 
};


// ======================================
// === MAIN COMPONENT ===
// ======================================
export default function Chat({ 
    currentChatId, 
    isStaff, 
    hotelId, 
    guestId, 
    chatName, 
    guestData, 
    scrollToUnread, 
    staffUsers = [], 
    onChatClose, 
    onMaximizeToggle, 
    isMaximized,
     
    isGuestEmbedded = false, 
    closeChat, 
    toggleMaximize, 
    isChatMaximized 
}) {
 
 
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [fileToUpload, setFileToUpload] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [messageCount, setMessageCount] = useState(MESSAGE_LIMIT);
    const [hasMore, setHasMore] = useState(true);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const [showOptionsId, setShowOptionsId] = useState(null); 
    const [hoveredMessageId, setHoveredMessageId] = useState(null); 
    
    const messagesEndRef = useRef(null);
    const firstUnreadRef = useRef(null);
    const isInitialLoad = useRef(true); 
    const prevChatIdRef = useRef(null); 
    const messageHistoryRef = useRef(null);
    
    const senderRole = isStaff ? 'staff' : 'guest';
    const senderId = isStaff ? (user?.uid || 'STAFF_ANON') : (guestId || 'GUEST_ANON');
    const myName = isStaff ?
    (user?.displayName || 'Staff Member') : (guestData?.fullName || 'You');

    const getBubbleClass = (msg) => {
        const isGuestMessage = msg.senderRole === 'guest';
        const isMyMessage = (isStaff && msg.senderId === senderId && msg.senderId !== 'SYSTEM_BOT') || (!isStaff && isGuestMessage);
        if (msg.senderId === 'SYSTEM_BOT') return 'system-bot';
        return isMyMessage ? 'my-message' : 'other-message';
    };
    const getSenderName = (msg) => {
        if (msg.senderId === senderId) return myName;
        if (msg.senderId === 'SYSTEM_BOT') return 'System Bot';
        if (isStaff && msg.senderRole === 'guest') return guestData?.fullName ||
    `Guest (Room ${guestData?.roomNumber || 'N/A'})`;
        
        const otherStaff = staffUsers.find(u => u.uid === msg.senderId);
        return otherStaff?.displayName || 'Staff Member';
    };
    const myLastMessage = [...messages].reverse().find(msg => 
        msg.senderId === senderId && !msg.isDeleted
    );
    // --------------------------------------------------------------------------------------
    
    /**
     * Simple function to open the file URL in a new tab. 
     * NOTE: This will fail with a 401 if the server requires authentication.
     */
    const handleOpenFile = useCallback((fileUrl) => {
        setShowOptionsId(null); 
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    }, []);

    /**
     * ULTIMATE DOWNLOAD FIX (Blob): Attempts to fetch the file as a Blob with CORS bypass headers.
     */
    const handleDownloadFile = useCallback(async (fileUrl, fileName) => {
        setShowOptionsId(null); 
        if (!fileUrl) return;

        try {
            // 1. Fetch the file content as a binary blob with a CORS-bypassing header (Origin: null)
            // This is a last-resort client-side bypass for strict server policies.
            const response = await fetch(fileUrl, {
                method: 'GET',
                // 🛑 NEW BYPASS ATTEMPT: This header can sometimes trick CORS policies 🛑
                headers: {
                    'Origin': 'null', 
                },
                // Use 'no-cors' mode only if absolutely necessary, but typically breaks Blob creation
                mode: 'cors' 
            });

            if (!response.ok) {
                // If fetch fails (e.g., 401 Unauthorized), notify and return
                console.error(`Secure download failed with status ${response.status}.`);
                alert(`Secure download failed. Status: ${response.status}. 
                
                This is a server authentication issue (401). 
                
                Please try the "Open File" option, or contact your developer to implement Cloudinary Signed URLs (the final fix).`);
                return;
            }

            const blob = await response.blob();
            
            // 2. Create a temporary local object URL for the blob
            const localUrl = URL.createObjectURL(blob);
            
            // 3. Programmatically create and click an anchor tag
            const link = document.createElement('a');
            link.href = localUrl;
            link.setAttribute('download', fileName);
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            
            // 4. Clean up the link element and revoke the object URL (CRITICAL for memory)
            document.body.removeChild(link);
            setTimeout(() => {
                 URL.revokeObjectURL(localUrl);
            }, 100); 

        } catch (error) {
            console.error("Critical Secure Download error:", error);
            alert("A critical error occurred during the secure download. The issue is on the file server side. Please ensure the file is set to public or use signed URLs.");
        }
    }, []);

    const scrollToBottom = (behavior = "smooth") => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: behavior });
            setShowJumpToBottom(false); 
        }, 10);
    };
    
    const smartScrollToInitialView = useCallback((isChatChange, shouldScrollToUnread) => {
        if (isChatChange) {
            scrollToBottom('auto'); 
        } else if (shouldScrollToUnread && firstUnreadRef.current) {
            firstUnreadRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            scrollToBottom('auto'); 
        }
    }, []);
    const handleScroll = useCallback(() => {
        const element = messageHistoryRef.current;
        if (element) {
            const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
            setShowJumpToBottom(distanceToBottom > SCROLL_THRESHOLD); 
        }
    }, []);
    const markMessagesAsRead = useCallback((msgs) => {
        if (!currentChatId || msgs.length === 0) return;
        
        const unreadMessages = msgs.filter(msg => {
            if (msg.read === true || msg.isDeleted) return false;
            if (!isStaff && (msg.senderRole === 'staff' || msg.senderId === 'SYSTEM_BOT')) return true; 
            if (isStaff && (msg.senderRole === 
    'guest' || msg.senderId === 'SYSTEM_BOT')) return true; 
            return false;
        });

        if (unreadMessages.length === 0) return;

        const batch = writeBatch(db);
        
        unreadMessages.forEach(msg => {
            const messageRef = doc(db, "chats", currentChatId, "messages", msg.id);
            
            batch.update(messageRef, { read: true });
        });
        
        let parentUpdate = {};
        if (isStaff) {
            parentUpdate.staffHasUnread = false;
        } else {
            parentUpdate.guestHasUnread = false;
        }

        if (Object.keys(parentUpdate).length > 0) {
 
            batch.update(doc(db, 'chats', currentChatId), parentUpdate);
        }
        
        batch.commit().catch(error => {
            console.error("Failed to mark messages as read:", error);
        });
    }, [currentChatId, isStaff]);


    const handleLoadMore = () => {
        isInitialLoad.current = false;
        setMessageCount(prevCount => prevCount + MESSAGE_LIMIT);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if(fileToUpload?.localUrl) URL.revokeObjectURL(fileToUpload.localUrl);
            const localUrl = URL.createObjectURL(file);
            setFileToUpload({ file, localUrl, type: file.type, fileName: file.name });
            e.target.value = null; 
        }
    };
    const clearFileToUpload = useCallback(() => {
        if(fileToUpload?.localUrl) URL.revokeObjectURL(fileToUpload.localUrl);
        setFileToUpload(null);
    }, [fileToUpload]);
    const handleDeleteMessage = async (messageId) => {
        setShowOptionsId(null); 
        if (!currentChatId || !isStaff) return;
        if (!window.confirm("Are you sure you want to permanently hide this message? This action cannot be undone.")) return;
        try {
            const messageRef = doc(db, "chats", currentChatId, "messages", messageId);
            await updateDoc(messageRef, {
                isDeleted: true
            });
        } catch (error) {
            console.error("Error marking message for deletion:", error);
            alert("Failed to delete message.");
        }
    };
    
    // EFFECT 1: Message Listener 
    useEffect(() => {
        if (!currentChatId) {
            setMessages([]);
            setMessageCount(MESSAGE_LIMIT); 
            setHasMore(true); 
            clearFileToUpload();
            return;
        
        }

        const messagesQuery = query(
             collection(db, "chats", currentChatId, "messages"),
            orderBy("timestamp", "desc"), 
            limit(messageCount) 
        );

        const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({
       
                id: doc.id,
                ...doc.data()
            })).filter(msg => !msg.isDeleted); 
            
            setHasMore(snapshot.size === messageCount);

            const sortedMsgs = msgs.reverse(); 
            
     
            const isChatChange = prevChatIdRef.current !== currentChatId;
            prevChatIdRef.current = currentChatId; 

            setMessages(sortedMsgs);
            markMessagesAsRead(sortedMsgs);
            if (isInitialLoad.current || isChatChange) {
                isInitialLoad.current = false;
                smartScrollToInitialView(isChatChange, scrollToUnread); 
            } else {
                const latestMsg = sortedMsgs[sortedMsgs.length - 1];
                if (latestMsg && latestMsg.senderId === senderId) {
                    scrollToBottom();
                }
            }
        });
        return () => {
            unsubscribe();
            isInitialLoad.current = true;
            setShowJumpToBottom(false);
            setShowOptionsId(null); 
            setHoveredMessageId(null); // Clear hover state on chat switch
        };
    }, [currentChatId, isStaff, markMessagesAsRead, messageCount, smartScrollToInitialView, scrollToUnread, senderId, clearFileToUpload]);
    // EFFECT 2 & 3: Scroll and Click-Outside Listeners
    useEffect(() => {
        const chatDiv = messageHistoryRef.current;
        if (chatDiv) {
            handleScroll(); 
            chatDiv.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (chatDiv) {
      
                chatDiv.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, currentChatId, messages.length]);
    useEffect(() => {
        const closeMenuOnBodyClick = (e) => {
            const menuBtn = e.target.closest('.message-options-btn');
            const menuContent = e.target.closest('.message-options-menu');

            if (showOptionsId && !menuBtn && !menuContent) {
                setShowOptionsId(null);
            }
        };

 
        document.addEventListener('click', closeMenuOnBodyClick);
        return () => {
            document.removeEventListener('click', closeMenuOnBodyClick);
        };
    }, [showOptionsId]);
    const handleSend = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const messageText = newMessage.trim();
        if (!currentChatId || !senderId || (!messageText && !fileToUpload)) return; 
        if (isUploading) return;
        
        const fileToSave = fileToUpload;
        setNewMessage(''); 
        clearFileToUpload(); 
        
        let fileData = null;
        let finalDownloadURL = null;
        // --- 1. HANDLE FILE UPLOAD TO CLOUDINARY ---
        if (fileToSave) {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('file', fileToSave.file);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            formData.append('tags', `chat_${currentChatId}`);
            try {
                const response = await fetch(CLOUDINARY_UPLOAD_URL, {
                    method: 'POST',
                    body: formData,
                });
                if (!response.ok) {
                    throw new Error(`Cloudinary upload failed with status: ${response.status}`);
                }

                const result = await response.json();
                finalDownloadURL = result.secure_url; 
                
                fileData = { 
                    url: finalDownloadURL, 
                    type: fileToSave.type,
                    fileName: fileToSave.fileName,
                };
            } catch (error) {
                console.error("Error uploading file to Cloudinary:", error);
                alert("File upload failed. Message cancelled.");
                setIsUploading(false);
                return; 
            } finally {
                setIsUploading(false);
            }
        }
        // --- END FILE UPLOAD ---
        

        try {
            const newMessageRef = await addDoc(collection(db, "chats", currentChatId, "messages"), {
                text: messageText,
                senderId: senderId,
        
                senderRole: senderRole,
                timestamp: serverTimestamp(),
                read: false,
                file: fileData, 
            });
            let updatePayload = {
                lastMessageText: messageText ||
            (fileData ? `[FILE] ${fileData.fileName}` : '...'), 
                lastMessageTimestamp: serverTimestamp(),
                lastSenderId: senderId, 
                lastMessageId: newMessageRef.id 
            };
            if (isStaff) {
                updatePayload.guestHasUnread = true;
                updatePayload[`staffLastViewed.${user.uid}`] = serverTimestamp();
            } else {
                updatePayload.staffHasUnread = true;
            }
            
            await updateDoc(doc(db, 'chats', currentChatId), updatePayload);
            if (!isStaff && hotelId && messageText) { 
                setTimeout(async () => {
                    await checkAndSendBotReply(currentChatId, hotelId, messageText);
                }, 500);
            }
            
            scrollToBottom();
        } catch (error) {
            console.error("Error saving message:", error);
            alert("Failed to send message to database.");
        }
    };
    
    let foundFirstUnread = false; 
    
    const isSidebarVisible = !!currentChatId ||
    isGuestEmbedded;
    const headerTitle = isStaff 
        ?
    (chatName || getSenderName({senderRole: 'guest', senderId: guestId}) || 'Select Chat') 
        : 'Staff Communication Hub';
    if (!isSidebarVisible) {
        return (
            <div className={`chat-container empty-state-container`}>
                <div className="chat-header">
                    <div className="chat-header-info"> 
                        <h4 className="chat-header-title">{headerTitle}</h4>
           
                    </div>
                </div>
                <div className="chat-empty-state">
                    <p>Select a conversation to start chatting.</p>
                </div>
            </div>
     
        );
    }
    
    // --- MAIN RENDER ---
    return (
        <div className={`chat-sidebar-wrapper ${isSidebarVisible ? 'is-open' : 'is-closed'} ${isGuestEmbedded ? 'guest-embedded' : ''}`}
            style={{display: 'flex', flexDirection: 'column', height: 'calc(100% - 0px)', marginTop: '50px', boxSizing: 'border-box'}}
        >
            <div className={`chat-container ${isMaximized || isChatMaximized ? 'is-maximized' : ''} ${isGuestEmbedded ? 'guest-embedded-chat' : ''}`}
        
            style={{display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' ,marginTop: '60px',boxSizing: 'border-box'}}
            > 
          
                {/* HEADERS */}
            {/* Guest/Embedded View Header (working in GuestStatus) */}
            {isGuestEmbedded && ( 
                <div style={{ backgroundColor: 'var(--color-primary, #1e90ff)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', flexShrink: 0, }} > 
                    <strong style={{marginLeft: '5px'}}>Staff Communication Hub</strong> 
                    <div> 
                        <button onClick={toggleMaximize} title={isChatMaximized ? 'Minimize Chat' : 'Maximize Chat'} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.5em', padding: '5px 10px', marginRight: '10px' }} > 
                            {isChatMaximized ? '⤢' : '⤡'} 
                        </button> 
                        <button 
                            onClick={() => closeChat()} // ⬅️ Robust fix applied to Guest button
                            title="Hide Chat" 
                            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '2em', fontWeight: 'lighter', padding: '5px 10px' }} 
                        > 
                            &times; 
                        </button> 
                    </div> 
                </div> 
            )} 

            {/* Staff/Dashboard View Header (THE FIX FOR HOTEL DASHBOARD IS HERE) */}
            {!(isGuestEmbedded) && 
            ( <div className="chat-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #ddd', flexShrink: 0, backgroundColor: '#f9f9f9'}}> 
                <div className="chat-header-info"> 
                    <h4 className="chat-header-title" style={{margin: 0, fontSize: '1.1em'}}>{headerTitle}</h4> 
                    {currentChatId && isUserOnline(isStaff ?
                    guestId : senderId) && ( <span className="online-status-text" style={{fontSize: '0.8em', color: '#00cc00', marginLeft: '5px'}}>• Online</span> )} 
                </div> 
                {isStaff && ( 
                    <div className="chat-controls"> 
                        {/* MAXIMIZE BUTTON (Works with toggleMaximize) */}
                        <button 
                            className="chat-maximize-btn" 
                            onClick={toggleMaximize} 
                            title={isChatMaximized ? "Restore Chat Panel" : "Maximize Chat Panel"} 
                            style={{
                                border: 'none', 
                                background: 'none', 
                                cursor: 'pointer', 
                                color: '#666', 
                                fontSize: '1.2em', 
                                marginRight: '10px' 
                            }}
                        >
                            {isChatMaximized ? '⤢' : '⤡'}
                        </button>
                        
                        {/* 🎯 STAFF CLOSE BUTTON FIX (visible in HotelDashboard) */}
                        <button 
                            className="chat-close-btn" 
                            onClick={() => closeChat()} // ⬅️ Applying the robust fix here
                            title="Close Chat Panel" 
                            style={{ 
                                border: 'none', 
                                background: 'none', 
                                cursor: 'pointer', 
                                color: 'red', 
                                fontSize: '1.5em', 
                                fontWeight: 'bold', 
                                lineHeight: '1', 
                                padding: '0 5px' 
                            }}
                        >
                            &times;
                        </button>
                    </div> 
                )}
            </div> )}
            
 
                {/* Message History (The Scrolling Area) */}
                <div 
                    className="message-history-area" 
                    ref={messageHistoryRef} 
                   
                    style={{overflowY: 'auto', flexGrow: 1, padding: '10px', minHeight: '0', boxSizing: 'border-box'}} 
                > 
                    
                    {/* Load More Button */}
                    {messages.length > 0 && hasMore && (
  
                        <div className="load-more-container" style={{textAlign: 'center', marginBottom: '10px'}}>
                            <button onClick={handleLoadMore} className="load-more-btn" style={{background: '#eee', border: '1px solid #ddd', borderRadius: '15px', padding: '5px 15px', cursor: 'pointer', fontSize: '0.85em'}}>
                            
                                Load Older Messages ({MESSAGE_LIMIT} more)
                            </button>
                        </div>
                    )}
                 
   
                  
                    {messages.length === 0 && (
                        <p className="chat-empty-text" style={{textAlign: 'center', color: '#666', marginTop: '20px'}}>
                            
                            No messages yet. Start the conversation!
                        </p>
                    )}
                    
                    {/* UPLOAD PROGRESS/STATUS */}
                    {isUploading && (
      
                        <div className="message-bubble-wrapper align-right" style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px'}}>
                             <div className="message-content-container" style={{maxWidth: '75%'}}>
                                
                                <div className="message-bubble my-message sending-status" style={{padding: '10px 12px', borderRadius: '15px 15px 0 15px', backgroundColor: '#007bff', color: 'white'}}>
                                    <p>Uploading file to server... Please wait.</p>
                                </div>
                      
                            </div>
                        </div>
                    )}


                    {messages.map((msg) => {
                        const bubbleClass = getBubbleClass(msg);
 
                        const isMyMessage = bubbleClass === 'my-message';
                        const isSystemBot = msg.senderId === 'SYSTEM_BOT';
                        
                        const isUnread = msg.read === false;
                        const showUnreadHighlight = isUnread && !isMyMessage;
                        let refToAssign = null;
                        if (showUnreadHighlight && !foundFirstUnread) {
                            refToAssign = firstUnreadRef;
                            foundFirstUnread = true;
                        }
                        
                        const senderName = getSenderName(msg);
                        const hasPublicURL = msg.file?.url && !msg.file.url.startsWith('blob:');
                        const isImageFile = msg.file?.type.startsWith('image/');

                        const messageContent = (
                            <>
                                {/* FILE DISPLAY LOGIC */}
                        
                                {msg.file && (
                                    <div 
                                        className={`file-attachment-container ${msg.text ? 'with-text' : ''}`}
        
                                        style={{
                                            padding: '8px', 
                      
                                            borderRadius: '5px',
                                            backgroundColor: isMyMessage ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                      
                                            marginBottom: msg.text ? '10px' : '0',
                                            display: 'flex',
                            
                                            flexDirection: 'column',
                                        }}
                                    >
       
                             
                                        {/* Image is now clickable again. */}
                                        {isImageFile && hasPublicURL ?
                                        (
                                            <div className="image-menu" title={`Image file: ${msg.file.fileName} - Click to open in new tab`} style={{marginBottom: '5px', display: 'block'}} >
                                                <img 
                                                    src={msg.file.url} 
                                                    alt={msg.file.fileName} 
                                                    className="live-image-attachment" 
                                                    style={{maxWidth: '100%', maxHeight: '200px', borderRadius: '5px', display: 'block', cursor: 'pointer'}} 
                                                    onClick={() => handleOpenFile(msg.file.url)} // <--- CLICK TO OPEN RESTORED
                                                />
                                            </div>
                                        ) : (
                                            <div className="file-placeholder" style={{ fontWeight: 'bold', padding: '5px 0', color: isMyMessage ? 'white' : 'black', display: 'flex', alignItems: 'center', }}>
                                                <span style={{fontSize: '1.2em', marginRight: '8px'}}>
                                                    {isImageFile ? '🖼️' : (msg.file.type.includes('pdf') ? '📄' : '📎')}
                                                </span>
                                                <span style={{flex: 1, whiteSpace: 'normal', wordBreak: 'break-all'}}>
                                                    {msg.file.fileName}
                                                </span>
                                            </div>
                                        )}
                                        {hasPublicURL && (
                                            <span style={{display: 'block', fontSize: '0.8em', color: isMyMessage ? 'white' : '#666', marginTop: '5px', opacity: 0.8}}>
                                                Use the **⋮ menu** for options. 
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* END FILE DISPLAY */}
                                {msg.text && <p style={{margin: msg.file ?
                                '10px 0 0 0' : 0, whiteSpace: 'pre-wrap'}}>{msg.text}</p>}
                                <div className="message-footer" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: msg.file ||
                                msg.text ? '5px' : '0' }}>
                                    <span className="message-timestamp" style={{fontSize: '0.75em', opacity: 0.7}}>
                                        {msg.timestamp?.toDate ?
                                        new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Sending...'}
                                    </span>
                                    {isMyMessage && msg.read === true && msg.id === myLastMessage?.id && (
                                        <span className="read-receipt-icon" title="Read" style={{marginLeft: '5px', fontSize: '0.8em'}}> &#x2714;&#x2714; </span>
                                    )}
                                </div>
                            </>
                        );
                        return (
                            <div 
                                key={msg.id} 
                                ref={refToAssign} 
                                className={`message-bubble-wrapper ${isMyMessage ? 'align-right' : 'align-left'} ${showUnreadHighlight ? 'unread-highlight-wrapper' : ''}`}
                                // Add event handlers to enable hover for the options button
                                onMouseEnter={() => setHoveredMessageId(msg.id)} 
                                onMouseLeave={() => setHoveredMessageId(null)}
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    marginBottom: '10px', 
                                    position: 'relative', 
                                    justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                                }}
                            >
                                {/* Avatar (omitted for brevity) */}
                                {(!isMyMessage || isStaff) && !isSystemBot && <Avatar name={senderName} isMyAvatar={isMyMessage} />}
                                
                                {/* CRITICAL POSITION FIX: KEBAB BUTTON */}
                                <button 
                                    onClick={() => setShowOptionsId(showOptionsId === msg.id ? null : msg.id)}
                                    className="message-options-btn"
                                    title="Message Options"
                                    style={{ 
                                        position: 'absolute', 
                                        top: '10px', 
                                        // FINAL FIX: Use -40px offset
                                        right: isMyMessage ? '-40px' : 'auto', 
                                        left: isMyMessage ? 'auto' : '-40px', 
                                        
                                        background: 'none', 
                                        border: 'none', 
                                        color: '#666', 
                                        fontSize: '1.2em', 
                                        cursor: 'pointer', 
                                        zIndex: 10, 
                                        padding: '5px', 
                                        lineHeight: 0, 
                                        transition: 'opacity 0.2s', 
                                        opacity: (hoveredMessageId === msg.id || showOptionsId === msg.id) ? 1 : 0, 
                                    }}
                                >
                                    &#x22EE;
                                </button>
                                
                                <div className="message-content-container" style={{
                                    position: 'relative', 
                                    maxWidth: '75%',
                                    // FINAL FIX: Add margin to the bubble to push it away from the absolute-positioned button
                                    marginRight: isMyMessage ? '30px' : '0', 
                                    marginLeft: isMyMessage ? '0' : '30px',
                                }}>
                                    {/* Sender Label (omitted for brevity) */}
                                    {(!isMyMessage) && ( 
                                        <span className={`sender-label ${isSystemBot 
                                        ? 'system-label' : ''}`} style={{display: 'block', marginBottom: '2px', fontSize: '0.85em', color: isSystemBot ? '#666' : '#999', textAlign: isMyMessage ? 'right' : 'left'}}>
                                            {senderName}
                                        </span>
                                    )}
                                    
                                    {/* KEBAB MENU CONTENT - FIXED POSITIONING */}
                                    {showOptionsId === msg.id && (
                                        <div 
                                            className="message-options-menu" 
                                            // Menu is positioned relative to the message-content-container
                                            style={{ 
                                                position: 'absolute', 
                                                top: isMyMessage ? '5px' : '25px', 
                                                // FIX: Position menu outside the bubble
                                                right: isMyMessage ? '100%' : 'auto', 
                                                left: isMyMessage ? 'auto' : '100%', 
                                                transform: isMyMessage ? 'translateX(-10px)' : 'translateX(10px)', 
                                                backgroundColor: 'white', 
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)', 
                                                border: '1px solid #ddd', 
                                                borderRadius: '5px', 
                                                padding: '0', 
                                                minWidth: '170px', // Increased width for new options
                                                zIndex: 10, 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                            }}
                                        >
                                            
                                            {/* OPTION 1: OPEN/VIEW (For all files) */}
                                            {msg.file?.url && hasPublicURL && !isImageFile && (
                                                <button 
                                                    onClick={() => handleOpenFile(msg.file.url)} 
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        padding: '10px', 
                                                        textAlign: 'left', 
                                                        cursor: 'pointer', 
                                                        color: '#333', 
                                                        borderBottom: '1px solid #eee', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        width: '100%' 
                                                    }}
                                                >
                                                    <span style={{marginRight: '8px'}}>👁️</span> Open File in New Tab
                                                </button>
                                            )}
                                            
                                            {/* OPTION 2: DOWNLOAD (Secure Blob method) */}
                                            {msg.file?.url && hasPublicURL && (
                                                <button 
                                                    onClick={() => handleDownloadFile(msg.file.url, msg.file.fileName)} 
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        padding: '10px', 
                                                        textAlign: 'left', 
                                                        cursor: 'pointer', 
                                                        color: '#333', 
                                                        borderBottom: isStaff ? '1px solid #eee' : 'none', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        width: '100%' 
                                                    }}
                                                >
                                                    <span style={{marginRight: '8px'}}>⬇️</span> Download File ({isImageFile ? 'Working' : 'Secure'})
                                                </button>
                                            )}

                                            {/* Delete Option (Staff Only) */}
                                            {isStaff && (
                                                <button 
                                                    onClick={() => handleDeleteMessage(msg.id)} 
                                                    style={{ 
                                                        background: 'none', 
                                                        border: 'none', 
                                                        padding: '10px', 
                                                        textAlign: 'left', 
                                                        cursor: 'pointer', 
                                                        color: 'red', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        width: '100%' 
                                                    }}
                                                >
                                                    <span style={{marginRight: '8px'}}>❌</span> Delete Message
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div 
                                        className={`message-bubble ${bubbleClass} ${showUnreadHighlight ?
                                        'unread-highlight' : ''}`} 
                                        style={{ 
                                            padding: '10px 12px', 
                                            borderRadius: isMyMessage ?
                                            '15px 15px 0 15px' : '15px 15px 15px 0', 
                                            backgroundColor: isMyMessage ? 'var(--color-primary, #1e90ff)' : '#f0f0f0', 
                                            color: isMyMessage ?
                                            'white' : 'black', 
                                            boxShadow: '0 1px 1px rgba(0,0,0,0.1)', 
                                            display: 'block', 
                                            wordBreak: 'break-word', 
                                            minWidth: '100px', 
                                        }}
                                    >
                                        {messageContent}
                                    </div>
                                </div>
                                {/* Avatar (omitted for brevity) */}
                                {isMyMessage && !isStaff && <Avatar name={myName} isMyAvatar={isMyMessage} />}
                            </div>
                        );
                    })}
                    
                    {/* --- LOCAL FILE PREVIEW --- */}
                    {fileToUpload && (
                        <div className="message-bubble-wrapper align-right local-preview" style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', opacity: isUploading ? 0.5 : 1}}>
                            <div className="message-content-container" style={{maxWidth: '75%'}}>
                                <button onClick={clearFileToUpload} title="Remove File" style={{position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '25px', height: '25px', cursor: 'pointer', zIndex: 5, fontSize: '1em', fontWeight: 'bold', padding: 0, lineHeight: '25px'}}>
                                    &times;
                                </button>
                                <div className="message-bubble my-message file-preview-bubble" style={{padding: '10px 12px', borderRadius: '15px 15px 0 15px', backgroundColor: '#007bff', color: 'white', display: 'block', wordBreak: 'break-word'}}>
                                    {fileToUpload.type.startsWith('image/') ? (
                                        <div style={{marginBottom: '10px'}}>
                                            <img src={fileToUpload.localUrl} alt="Preview" style={{maxWidth: '100%', maxHeight: '150px', borderRadius: '5px', display: 'block'}} />
                                        </div>
                                    ) : (
                                        <div style={{ fontWeight: 'bold', padding: '5px 0', color: 'white', display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                                            <span style={{fontSize: '1.2em', marginRight: '8px'}}>
                                                {fileToUpload.type.includes('pdf') ? '📄' : '📎'}
                                            </span>
                                            <span>{fileToUpload.fileName}</span>
                                        </div>
                                    )}
                                    <p style={{margin: 0, whiteSpace: 'pre-wrap'}}>{newMessage.trim() || 'No caption'}</p>
                                    <div className="file-info-text" style={{fontSize: '0.8em', opacity: 0.8, margin: '5px 0', color: 'white'}}>📎 File selected: {fileToUpload.file.name} (Ready to send)</div>
                                    <div className="message-footer" style={{marginTop: '5px'}}>
                                        <span className="message-timestamp" style={{fontSize: '0.75em', opacity: 0.7}}>Preview...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* --- END LOCAL FILE PREVIEW --- */}
                    <div ref={messagesEndRef} />
                </div>

                {/* JUMP TO BOTTOM BUTTON (omitted for brevity) */}
                {showJumpToBottom && (
                    <button onClick={() => scrollToBottom()} className="jump-to-bottom-btn" title="Jump to latest message" style={{ 
                        position: 'absolute', 
                        bottom: '80px', 
                        right: '20px', 
                        zIndex: 10, 
                        backgroundColor: 'var(--color-primary, #1e90ff)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '50%', 
                        width: '40px', 
                        height: '40px', 
                        cursor: 'pointer', 
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)', 
                        fontSize: '1.2em' 
                    }} >
                        ↓
                    </button>
                )}

                {/* Message Input (Fixed 
                Footer) */}
                <form onSubmit={handleSend} className="chat-input-area" style={{ 
                    display: 'flex', 
                    padding: '10px', 
                    borderTop: '1px solid #ccc', 
                    flexShrink: 0,
                    alignItems: 'center', 
                    backgroundColor: 'white'
                }}>
                    <input 
                        type="file" 
                        id="fileInput" 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                        disabled={!currentChatId || isUploading}
                    />
                    
                    <label htmlFor="fileInput" className="file-upload-label" title="Attach file">
                        <span style={{ fontSize: '1.5em', cursor: 'pointer', padding: '0 10px', color: '#666' }}>
                            📎
                        </span>
                    </label>
                    
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { 
                                e.preventDefault();
                                if (currentChatId && (newMessage.trim() || fileToUpload) && !isUploading) {
                                    handleSend(e); 
                                }
                            }
                        }}
                        placeholder={currentChatId ? (fileToUpload ? "Add a caption or press Enter/Send" : "Type a message...") : "Select a chat to start..."}
                        disabled={!currentChatId || isUploading}
                        style={{
                            flex: 1, 
                            padding: '10px', 
                            borderRadius: '20px', 
                            border: '1px solid #ddd', 
                            marginRight: '10px',
                            minHeight: '38px', 
                            boxSizing: 'border-box', 
                            width: 'auto' 
                        }}
                    />
                    <button 
                        type="submit" 
                        disabled={!currentChatId || (!newMessage.trim() && !fileToUpload) || isUploading}
                        style={{
                            padding: '8px 15px', 
                            borderRadius: '20px', 
                            border: 'none', 
                            backgroundColor: 'var(--color-primary, #1e90ff)', 
                            color: 'white', 
                            cursor: 'pointer', 
                            minWidth: '70px', 
                            height: '38px', 
                            fontSize: '0.9em',
                            boxSizing: 'border-box'
                        }} 
                    >
                        {isUploading ? '...' : 'Send'}
                    </button>
                </form>
            </div>
        </div>
    );
}